/** Word Scrambler logic: shuffles the letters of each word on a line, never the spaces. */

export type ScrambleMode = "all" | "keep-first" | "keep-first-last";

const MAX_RESHUFFLE_ATTEMPTS = 12;
const WHITESPACE_SPLIT = /(\s+)/;
const WHITESPACE_ONLY = /^\s+$/;

type Random = () => number;

/** How many letters at each end a mode leaves where they are. */
const LOCKED_ENDS: Record<ScrambleMode, { start: number; end: number }> = {
  all: { start: 0, end: 0 },
  "keep-first": { start: 1, end: 0 },
  "keep-first-last": { start: 1, end: 1 },
};

function fisherYatesShuffle(letters: string[], random: Random): string[] {
  const result = letters.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function scrambleOnce(letters: string[], mode: ScrambleMode, random: Random) {
  const { start, end } = LOCKED_ENDS[mode];
  const middle = letters.slice(start, letters.length - end);
  return [
    ...letters.slice(0, start),
    ...fisherYatesShuffle(middle, random),
    ...letters.slice(letters.length - end),
  ].join("");
}

/**
 * Scrambles one word. A word with fewer than two free letters (a single letter, two
 * letters in the keep-first mode, three or fewer in the keep-first-and-last mode) has
 * nothing that can move, so it comes back unchanged.
 */
export function scrambleWord(
  word: string,
  mode: ScrambleMode,
  random: Random = Math.random
): string {
  const letters = Array.from(word);
  const { start, end } = LOCKED_ENDS[mode];
  if (letters.length - start - end < 2) {
    return word;
  }
  let result = scrambleOnce(letters, mode, random);
  let attempts = 0;
  while (result === word && attempts < MAX_RESHUFFLE_ATTEMPTS) {
    result = scrambleOnce(letters, mode, random);
    attempts += 1;
  }
  return result;
}

/** Scrambles every word of a line on its own, keeping the original spacing. */
export function scrambleLine(
  line: string,
  mode: ScrambleMode,
  random: Random = Math.random
): string {
  return line
    .split(WHITESPACE_SPLIT)
    .map((part) =>
      part.length === 0 || WHITESPACE_ONLY.test(part)
        ? part
        : scrambleWord(part, mode, random)
    )
    .join("");
}
